import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/shadcn-ui/dialog';
import { Button } from '@/components/shadcn-ui/button';
import { FileText, ExternalLink, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

function DocumentUploadModal({ isOpen, onClose, onFetchDocuments, onSubmitDocuments }) {
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [uploads, setUploads] = useState({});
  const fileRefs = useRef({});

  useEffect(() => {
    if (!isOpen) return;
    setDocuments([]);
    setUploads({});
    setIsLoading(true);
    onFetchDocuments()
      .then(res => { setDocuments(res ?? []); })
      .catch(() => toast.error('Failed to load required documents.'))
      .finally(() => setIsLoading(false));
  }, [isOpen, onFetchDocuments]);

  const handleFileChange = (docName, e) => {
    const file = e.target.files[0];
    if (file) setUploads(prev => ({ ...prev, [docName]: [file] }));
    e.target.value = '';
  };

  const removeFile = docName => {
    setUploads(prev => ({ ...prev, [docName]: [] }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onSubmitDocuments(uploads);
      onClose();
    } catch {
      toast.error('Failed to submit documents.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg dark text-white">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">Upload Required Documents</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Upload the documents required for your permit application.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-4">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-10 gap-3 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="text-sm">Loading required documents...</span>
            </div>
          ) : documents.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">No documents required.</p>
          ) : (
            <div className="max-h-[340px] overflow-y-auto space-y-3 pr-1">
              {documents.map(doc => (
                <div key={doc.name} className="rounded border border-border p-3 space-y-4">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium">{doc.name}</span>
                    <a
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex shrink-0 items-center gap-1 text-sm text-primary underline"
                    >
                      Open form <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>

                  {uploads[doc.name]?.[0] && (
                    <div className="flex items-center gap-2 bg-muted px-2 py-2 rounded text-xs">
                      <FileText className="h-3 w-3 shrink-0" />
                      <span className="truncate flex-1">{uploads[doc.name][0].name}</span>
                      <button onClick={() => removeFile(doc.name)}>
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                  {!uploads[doc.name]?.[0] && (
                    <>
                      <input
                        ref={el => (fileRefs.current[doc.name] = el)}
                        type="file"
                        className="hidden"
                        multiple={false}
                        accept=".pdf,.doc,.docx,.txt,image/*"
                        onChange={e => handleFileChange(doc.name, e)}
                      />

                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full text-xs"
                        onClick={() => fileRefs.current[doc.name]?.click()}
                      >
                        Choose file
                      </Button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          {!isLoading && (
            <div className="flex gap-2 pt-1">
              <Button variant="outline" onClick={onClose} className="flex-1" disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                className="flex-1"
                disabled={isSubmitting || documents.length === 0}
              >
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Submit'}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default DocumentUploadModal;
